import { useEffect, useState, useMemo, useCallback } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Button, Card, CardContent, TextField, Chip, Avatar, Alert } from '@mui/material';
import { Wallet, TrendingUp, TrendingDown, ArrowUpRight, Tag, Calendar, Award, BarChart3, Plus, FileText, Layers, Activity, AlertCircle, ExternalLink, DollarSign, Filter } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, Legend } from 'recharts';
import { DashboardGrid } from '@/components/dashboard/DashboardGrid';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { QuickActionCard } from '@/components/dashboard/QuickActionCard';
import { PageHeader } from '@/components/navigation/PageHeader';
import { ErrorState } from '@/components/feedback/ErrorState';
import { Loader } from '@/components/feedback/Loader';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useAuth } from '@/hooks/useAuth';
import { useSchool } from '@/hooks/useSchool';
import { ROLES } from '@/constants/roles';
import { expenseService } from '@/services/expenseService';
import { useNavigate } from 'react-router-dom';
import { tokens } from '@/theme/tokens';
import toast from 'react-hot-toast';

const CATEGORY_COLORS = ['#2563EB', '#EC4899', '#F59E0B', '#16A34A', '#8B5CF6', '#06B6D4', '#E11D48', '#64748B'];
const CHART_COLOR = '#2563EB';

const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return '₹0';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const parts = dateStr.split('T')[0].split('-');
  if (parts.length < 3) return dateStr;
  const [year, month, day] = parts;
  const d = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  return d.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const formatDateToISO = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DATE_PRESET_OPTIONS = [
  { value: 'thisMonth', label: 'This Month' },
  { value: 'today', label: 'Today' },
  { value: '7days', label: '7 Days' },
  { value: '15days', label: '15 Days' },
  { value: '1month', label: 'One Month' },
  { value: '2months', label: '2 Months' },
  { value: 'custom', label: 'Custom' },
];

const getPresetDates = (preset) => {
  const now = new Date();
  const todayStr = formatDateToISO(now);

  switch (preset) {
    case 'today':
      return { from: todayStr, to: todayStr };
    case '7days': {
      const d = new Date(now);
      d.setDate(d.getDate() - 6);
      return { from: formatDateToISO(d), to: todayStr };
    }
    case '15days': {
      const d = new Date(now);
      d.setDate(d.getDate() - 14);
      return { from: formatDateToISO(d), to: todayStr };
    }
    case 'thisMonth': {
      const d = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: formatDateToISO(d), to: todayStr };
    }
    case '1month': {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 1);
      return { from: formatDateToISO(d), to: todayStr };
    }
    case '2months': {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 2);
      return { from: formatDateToISO(d), to: todayStr };
    }
    default:
      return null;
  }
};

// Unified stat card with consistent design tokens
function DashboardStatCard({ title, value, subtext, subtextColor, icon: Icon, color = 'primary', onClick }) {
  const colorMap = {
    primary: tokens.colors.primary.main,
    success: tokens.colors.success.main,
    warning: tokens.colors.warning.main,
    error: tokens.colors.error.main,
    info: tokens.colors.info.main,
    secondary: '#EC4899',
    purple: '#8B5CF6',
  };
  const themeColor = colorMap[color] || (typeof color === 'string' && color.startsWith('#') ? color : tokens.colors.primary.main);

  return (
    <Card
      onClick={onClick}
      sx={{
        height: '100%',
        minHeight: 112,
        cursor: onClick ? 'pointer' : 'default',
        transition: `all ${tokens.transitions.normal}`,
        border: `1px solid ${tokens.colors.border}`,
        boxShadow: tokens.shadows.card,
        '&:hover': {
          boxShadow: tokens.shadows.md,
          transform: onClick ? 'translateY(-2px)' : 'none',
        },
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: tokens.colors.text.muted,
                mb: 0.5,
                fontWeight: 500,
                fontSize: '0.8125rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: tokens.colors.text.primary,
                lineHeight: 1.2,
                fontSize: '1.45rem',
                mb: 0.5,
              }}
            >
              {value}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: subtextColor || tokens.colors.text.muted,
                fontWeight: subtextColor ? 600 : 500,
                fontSize: '0.75rem',
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.2,
              }}
            >
              {subtext || '\u00A0'}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: tokens.radius.md,
              backgroundColor: `${themeColor}14`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              ml: 1.5,
            }}
          >
            <Icon size={22} color={themeColor} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// Insight row for Quick Insights widget
function InsightRow({ icon: Icon, title, value, color }) {
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, p: 1.75,
        borderRadius: tokens.radius.md,
        backgroundColor: tokens.colors.background,
        border: `1px solid ${tokens.colors.border}`,
      }}
    >
      <Box
        sx={{
          width: 38, height: 38, borderRadius: tokens.radius.full,
          backgroundColor: `${color}14`, display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}
      >
        <Icon size={19} color={color} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: tokens.colors.text.muted, fontWeight: 500, display: 'block', mb: 0.25 }}>
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600, color: tokens.colors.text.primary,
            lineHeight: 1.35, wordBreak: 'break-word',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

// Custom tooltip for charts
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const firstData = payload[0]?.payload;
  const title = firstData?.category_name || firstData?.dateLabel || label;

  return (
    <Box
      sx={{
        backgroundColor: tokens.colors.card, p: 1.5,
        borderRadius: tokens.radius.sm, boxShadow: tokens.shadows.md,
        border: `1px solid ${tokens.colors.border}`,
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5, color: tokens.colors.text.primary }}>
        {title}
      </Typography>
      {payload.map((item, i) => (
        <Typography key={i} variant="caption" sx={{ display: 'block', color: item.color || tokens.colors.text.secondary }}>
          {item.name}: <strong style={{ color: tokens.colors.text.primary }}>{formatCurrency(item.value)}</strong>
        </Typography>
      ))}
    </Box>
  );
}

// Top label on bar chart columns
const BarLabel = ({ x, y, width, value }) => (
  <text x={x + width / 2} y={y - 6} fill={tokens.colors.text.secondary} textAnchor="middle" fontSize={10} fontWeight={600}>
    {formatCurrency(value)}
  </text>
);

// In-slice percentage label for donut chart
const PieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const RADIAN = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);

  if (percent < 0.04) return null;
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

// Compact table styling matching StudentDashboardPage
const TABLE_SX = {
  width: '100%', borderCollapse: 'collapse',
  '& th': {
    py: 1.25, px: 1.5, textAlign: 'left', fontSize: '0.75rem', fontWeight: 600,
    color: tokens.colors.text.muted, borderBottom: `1px solid ${tokens.colors.border}`,
    whiteSpace: 'nowrap',
  },
  '& td': {
    py: 1.25, px: 1.5, fontSize: '0.8125rem', color: tokens.colors.text.primary,
    borderBottom: `1px solid ${tokens.colors.border}`, whiteSpace: 'nowrap',
  },
  '& tbody tr:hover': { backgroundColor: tokens.colors.grey[50], cursor: 'pointer' },
  '& tbody tr:last-child td': { borderBottom: 'none' },
};

export function ExpenseDashboardPage() {
  usePageMeta('Expense Management', [
    { label: 'Expenses', path: '/expenses' },
    { label: 'Dashboard' },
  ]);

  const { user } = useAuth();
  const navigate = useNavigate();
  const { schoolId } = useSchool();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);

  // Filters state
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('ALL');
  const [datePreset, setDatePreset] = useState('thisMonth');
  const [fromDate, setFromDate] = useState(() => getPresetDates('thisMonth')?.from || '');
  const [toDate, setToDate] = useState(() => getPresetDates('thisMonth')?.to || '');

  const handlePresetChange = (e) => {
    const val = e.target.value;
    setDatePreset(val);
    if (val !== 'custom') {
      const range = getPresetDates(val);
      if (range) {
        setFromDate(range.from);
        setToDate(range.to);
      }
    }
  };

  // Load categories once
  useEffect(() => {
    if (!schoolId) return;
    expenseService.getCategories(schoolId, true)
      .then((res) => {
        const list = res?.categories || res?.data || res || [];
        setCategories(Array.isArray(list) ? list : []);
      })
      .catch(console.error);
  }, [schoolId]);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    if (!schoolId) return;

    if (fromDate && toDate && fromDate > toDate) {
      const errorMsg = 'From Date cannot be later than To Date.';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const res = await expenseService.getDashboardOverview(schoolId, {
        from_date: fromDate,
        to_date: toDate,
        category_id: selectedCategoryId,
      });
      setDashboardData(res);
    } catch (err) {
      console.error('Failed to load expense dashboard overview:', err);
      const errorMsg = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to load expense dashboard';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [schoolId, fromDate, toDate, selectedCategoryId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Extract dashboard data properties safely
  const summary = dashboardData?.summary || {};
  const comparison = dashboardData?.previous_period_comparison || {};
  const categoryBreakdown = dashboardData?.category_breakdown || [];
  const dailyTrendRaw = dashboardData?.daily_trend || [];
  const recentExpenses = dashboardData?.recent_expenses || [];
  const categoryCoverage = dashboardData?.category_coverage || {};

  // Formatted daily trend
  const dailyTrend = useMemo(() => {
    return dailyTrendRaw.map((item) => ({
      ...item,
      dateLabel: formatDate(item.date),
    }));
  }, [dailyTrendRaw]);

  // Formatted donut data
  const pieChartData = useMemo(() => {
    return categoryBreakdown.map((c, i) => ({
      name: c.category_name,
      value: c.total_amount,
      percentage: c.percentage_of_total,
      color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
    }));
  }, [categoryBreakdown]);

  // Top 5 categories for cards
  const topCategories = useMemo(() => {
    return categoryBreakdown.slice(0, 5).map((c, i) => ({
      ...c,
      color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
    }));
  }, [categoryBreakdown]);

  // Quick insights
  const insights = useMemo(() => {
    const topCat = categoryBreakdown[0];
    const largestExp = dashboardData?.high_value_expenses?.[0];
    const unusedCount = categoryCoverage.categories_without_expenses?.length || 0;

    return [
      {
        icon: Award,
        title: 'Top Category',
        value: topCat ? `${topCat.category_name} (${formatCurrency(topCat.total_amount)})` : 'None',
        color: '#2563EB'
      },
      {
        icon: ArrowUpRight,
        title: 'Largest Expense',
        value: largestExp ? `${largestExp.description} – ${formatCurrency(largestExp.amount)}` : 'None',
        color: '#F59E0B'
      },
      {
        icon: Layers,
        title: 'Unused Categories',
        value: `${unusedCount} categories without expenses`,
        color: '#8B5CF6'
      },
      {
        icon: comparison.percentage_change >= 0 ? TrendingUp : TrendingDown,
        title: 'Period Growth',
        value: `${comparison.percentage_change >= 0 ? '+' : ''}${comparison.percentage_change ?? 0}% vs previous period`,
        color: comparison.percentage_change >= 0 ? '#DC2626' : '#16A34A'
      },
    ];
  }, [categoryBreakdown, dashboardData, categoryCoverage, comparison]);

  const canManage =
    user?.role === ROLES.ORGANIZATION_ADMIN ||
    user?.role === ROLES.PRINCIPAL ||
    user?.role === ROLES.ACCOUNTANT;

  const isInvalidDateRange = Boolean(fromDate && toDate && fromDate > toDate);

  if (loading && !dashboardData) return <Loader message="Loading expense dashboard..." />;

  if (error && !dashboardData) {
    return (
      <Box sx={{ p: 3 }}>
        <PageHeader title="Expense Dashboard" subtitle="Overview of school expenses and financial analytics" />
        <WidgetContainer sx={{ mt: 3, p: 4 }}>
          <ErrorState
            title="Failed to Load Expense Dashboard"
            message={error}
            onRetry={fetchDashboardData}
          />
        </WidgetContainer>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
      {/* Page Header & Filters */}
      <PageHeader
        title="Expense Dashboard"
        subtitle="Overview of school expenses and financial analytics"
        actions={
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Date Range</InputLabel>
              <Select
                value={datePreset}
                label="Date Range"
                onChange={handlePresetChange}
              >
                {DATE_PRESET_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {datePreset === 'custom' && (
              <>
                <TextField
                  size="small"
                  type="date"
                  label="From"
                  value={fromDate}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFromDate(val);
                    if (toDate && val && val > toDate) {
                      toast.error('From Date cannot be later than To Date');
                    }
                  }}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { max: toDate || undefined },
                  }}
                  error={isInvalidDateRange}
                  helperText={isInvalidDateRange ? 'Must be ≤ To Date' : undefined}
                  sx={{ width: 145 }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="To"
                  value={toDate}
                  onChange={(e) => {
                    const val = e.target.value;
                    setToDate(val);
                    if (fromDate && val && val < fromDate) {
                      toast.error('To Date cannot be earlier than From Date');
                    }
                  }}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { min: fromDate || undefined },
                  }}
                  error={isInvalidDateRange}
                  helperText={isInvalidDateRange ? 'Must be ≥ From Date' : undefined}
                  sx={{ width: 145 }}
                />
              </>
            )}

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategoryId}
                label="Category"
                onChange={(e) => setSelectedCategoryId(e.target.value)}
              >
                <MenuItem value="ALL">All Categories</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        }
      />

      {/* Error Banner when filter fetch fails */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{
            borderRadius: tokens.radius.md,
            border: `1px solid ${tokens.colors.error.main}30`,
            backgroundColor: `${tokens.colors.error.main}10`,
          }}
        >
          {error}
        </Alert>
      )}

      {/* Row 1: Primary Stat Cards */}
      <DashboardGrid columns={{ xs: 1, sm: 2, md: 3, lg: 5 }} gap={1.75} sx={{ mb: 0 }}>
        <DashboardStatCard
          title="Total Expenses"
          value={formatCurrency(summary.total_expenses)}
          subtext={`${summary.expense_count || 0} Total Expenses`}
          subtextColor={tokens.colors.primary.main}
          icon={Wallet}
          color="primary"
          onClick={() => navigate('/expenses')}
        />
        <DashboardStatCard
          title="Avg Expense"
          value={formatCurrency(summary.average_expense)}
          subtext="Per transaction avg"
          subtextColor={tokens.colors.info.main}
          icon={TrendingUp}
          color="info"
          onClick={() => navigate('/expenses')}
        />
        <DashboardStatCard
          title="Largest Expense"
          value={formatCurrency(summary.largest_expense)}
          subtext="Highest single transaction"
          subtextColor={tokens.colors.warning.main}
          icon={ArrowUpRight}
          color="warning"
          onClick={() => navigate('/expenses')}
        />
        <DashboardStatCard
          title="Period Comparison"
          value={`${comparison.percentage_change >= 0 ? '+' : ''}${comparison.percentage_change ?? 0}%`}
          subtext={`${formatCurrency(Math.abs(comparison.amount_change || 0))} vs prev period`}
          subtextColor={comparison.percentage_change >= 0 ? tokens.colors.error.main : tokens.colors.success.main}
          icon={comparison.percentage_change >= 0 ? TrendingUp : TrendingDown}
          color={comparison.percentage_change >= 0 ? 'error' : 'success'}
        />
        <DashboardStatCard
          title="Active Categories"
          value={`${categoryCoverage.active_categories || 0} / ${categoryCoverage.total_categories || 0}`}
          subtext={`${categoryCoverage.categories_without_expenses?.length || 0} unused categories`}
          subtextColor="#8B5CF6"
          icon={Tag}
          color="purple"
          onClick={() => navigate('/expenses/categories')}
        />
      </DashboardGrid>

      {/* Row 2: Top Categories Cards */}
      {topCategories.length > 0 && (
        <DashboardGrid columns={{ xs: 1, sm: 2, md: 3, lg: 5 }} gap={1.75} sx={{ mb: 0 }}>
          {topCategories.map((c) => (
            <DashboardStatCard
              key={c.category_id}
              title={c.category_name}
              value={formatCurrency(c.total_amount)}
              subtext={`${c.percentage_of_total}% of total (${c.expense_count} txns)`}
              subtextColor={c.color}
              icon={Tag}
              color={c.color}
              onClick={() => {
                setSelectedCategoryId(c.category_id);
              }}
            />
          ))}
        </DashboardGrid>
      )}

      {/* Row 3: Bar Chart + Donut Chart */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 1.75 }}>
        <WidgetContainer
          title="Expenses by Category"
          action={
            <Button size="small" variant="outlined" onClick={() => navigate('/expenses/categories')}>
              View Categories
            </Button>
          }
        >
          <Box sx={{ width: '100%', height: 300 }}>
            {categoryBreakdown.length > 0 ? (
              <ResponsiveContainer>
                <BarChart data={categoryBreakdown} margin={{ top: 20, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={tokens.colors.border} />
                  <XAxis
                    dataKey="category_name" tick={{ fontSize: 11, fill: tokens.colors.text.muted }}
                    axisLine={false} tickLine={false} interval={0}
                    angle={-25} textAnchor="end" height={45}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: tokens.colors.text.muted }}
                    axisLine={false} tickLine={false}
                    tickFormatter={(val) => `₹${val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}`}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar
                    dataKey="total_amount" name="Total Amount"
                    fill={CHART_COLOR} radius={[4, 4, 0, 0]}
                    label={<BarLabel />} maxBarSize={45}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography variant="body2" color="text.secondary">No category breakdown data available</Typography>
              </Box>
            )}
          </Box>
        </WidgetContainer>

        <WidgetContainer
          title="Category Distribution"
          action={
            <Button size="small" sx={{ textTransform: 'none', color: tokens.colors.primary.main }} onClick={() => navigate('/expenses')}>
              View All
            </Button>
          }
        >
          <Box sx={{ width: '100%', height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {pieChartData.length > 0 ? (
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%" cy="45%"
                    innerRadius={55} outerRadius={95}
                    paddingAngle={3} dataKey="value"
                    label={PieLabel} labelLine={false}
                  >
                    {pieChartData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                  <Legend
                    verticalAlign="bottom"
                    iconType="circle" iconSize={10}
                    formatter={(val) => {
                      const item = pieChartData.find((g) => g.name === val);
                      return (
                        <span style={{ fontSize: 11, color: tokens.colors.text.secondary }}>
                          {val}&nbsp;&nbsp;{formatCurrency(item?.value ?? 0)} ({item?.percentage ?? 0}%)
                        </span>
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">No distribution data available</Typography>
            )}
          </Box>
        </WidgetContainer>
      </Box>

      {/* Row 4: Recent Expenses + Daily Trend + Quick Insights */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.45fr 0.9fr 0.65fr' }, gap: 1.75 }}>
        <WidgetContainer
          title="Recent Expenses"
          noPadding
          action={
            <Button
              size="small"
              sx={{ textTransform: 'none', color: tokens.colors.primary.main }}
              onClick={() => navigate('/expenses')}
            >
              View All
            </Button>
          }
        >
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={TABLE_SX}>
              <thead>
                <tr>
                  <th style={{ width: 28, textAlign: 'center' }}>#</th>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Category</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {recentExpenses.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: tokens.colors.text.muted, py: 3 }}>
                      No recent expenses
                    </td>
                  </tr>
                ) : (
                  recentExpenses.map((exp, idx) => (
                    <tr key={exp.expense_id || idx} onClick={() => navigate('/expenses')}>
                      <td style={{ width: 28, textAlign: 'center', color: tokens.colors.text.muted, fontSize: '0.75rem' }}>
                        {idx + 1}
                      </td>
                      <td>
                        <Typography variant="caption" sx={{ color: tokens.colors.text.secondary, whiteSpace: 'nowrap' }}>
                          {formatDate(exp.expense_date)}
                        </Typography>
                      </td>
                      <td>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                          {exp.description || 'Expense record'}
                        </Typography>
                      </td>
                      <td>
                        <Chip
                          label={exp.category_name}
                          size="small"
                          sx={{ fontSize: '0.7rem', fontWeight: 600, height: 20, bgcolor: tokens.colors.grey[100] }}
                        />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: tokens.colors.text.primary, whiteSpace: 'nowrap' }}>
                          {formatCurrency(exp.amount)}
                        </Typography>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Box>
          </Box>
        </WidgetContainer>

        <WidgetContainer title="Daily Expense Trend">
          <Box sx={{ width: '100%', height: 250 }}>
            {dailyTrend.length > 0 ? (
              <ResponsiveContainer>
                <AreaChart data={dailyTrend} margin={{ top: 10, right: 10, left: -15, bottom: 5 }}>
                  <defs>
                    <linearGradient id="expenseGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLOR} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={CHART_COLOR} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={tokens.colors.border} />
                  <XAxis
                    dataKey="dateLabel"
                    tick={{ fontSize: 10, fill: tokens.colors.text.muted }}
                    axisLine={false} tickLine={false} interval={0}
                    angle={-25} textAnchor="end" height={40}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: tokens.colors.text.muted }}
                    axisLine={false} tickLine={false}
                    tickFormatter={(val) => `₹${val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}`}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Area
                    type="monotone" dataKey="total_amount" name="Expense Amount"
                    stroke={CHART_COLOR} fill="url(#expenseGrad)" strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography variant="body2" color="text.secondary">No daily trend available</Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 2, pt: 2, borderTop: `1px solid ${tokens.colors.border}` }}>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: tokens.colors.text.muted }}>Period Total</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: tokens.colors.primary.main }}>
                {formatCurrency(summary.total_expenses)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: tokens.colors.text.muted }}>Active Days</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {dailyTrend.length} Days
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: tokens.colors.text.muted }}>Peak Day Spend</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: tokens.colors.warning.main }}>
                {formatCurrency(Math.max(...dailyTrend.map((d) => d.total_amount || 0), 0))}
              </Typography>
            </Box>
          </Box>
        </WidgetContainer>

        <WidgetContainer title="Quick Insights">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {insights.map((item, i) => (
              <InsightRow key={i} {...item} />
            ))}
          </Box>
        </WidgetContainer>
      </Box>

      {/* Quick Actions (Admin/Principal/Accountant only) */}
      {canManage && (
        <WidgetContainer title="Quick Actions">
          <Box sx={{ p: 1 }}>
            <DashboardGrid columns={{ xs: 1, sm: 2, md: 4 }} gap={1}>
              <QuickActionCard title="View All Expenses" icon={Wallet} path="/expenses" color="primary" />
              <QuickActionCard title="Expense Categories" icon={Tag} path="/expenses/categories" color="success" />
              <QuickActionCard title="Expense Reports" icon={FileText} color="secondary" />
              <QuickActionCard title="Financial Analytics" icon={Activity} color="warning" />
            </DashboardGrid>
          </Box>
        </WidgetContainer>
      )}
    </Box>
  );
}
